import type { CanonicalMessageContextDisplay } from '../components/ai-chat-chips/canonical-message-context';
import type { StreamObject } from '../components/ai-chat-messages';

export type AssistantSourceItemKind =
  | 'doc-search'
  | 'mail'
  | 'web'
  | 'created-doc'
  | (string & {});

export interface AssistantSourceItem {
  id: string;
  kind: AssistantSourceItemKind;
  label: string;
  display: CanonicalMessageContextDisplay;
  refs: Record<string, unknown>;
  payload?: Record<string, unknown> | null;
}

export interface AssistantSourceManifest {
  items: AssistantSourceItem[];
}

export interface ReusableTurnSource {
  key: string;
  label: string;
  url?: string;
  snippet?: string;
}

export function extractAssistantSourceManifest(
  streamObjects: StreamObject[]
): AssistantSourceManifest {
  const items: AssistantSourceItem[] = [];
  const seen = new Set<string>();

  const push = (item: AssistantSourceItem) => {
    const key = `${item.kind}:${item.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  for (const streamObject of streamObjects) {
    if (streamObject.type !== 'tool-result') continue;

    switch (streamObject.toolName) {
      case 'doc_semantic_search':
      case 'doc_keyword_search':
        extractDocSearchSourceItems(streamObject).forEach(push);
        break;
      case 'doc_read':
        extractDocReadSourceItems(streamObject).forEach(push);
        break;
      case 'database_read':
        extractDatabaseReadSourceItems(streamObject).forEach(push);
        break;
      case 'blob_read':
        extractBlobReadSourceItems(streamObject).forEach(push);
        break;
      case 'mail_search':
      case 'mail_list_drafts':
      case 'mail_get_message':
      case 'mail_summarize':
      case 'mail_draft_reply':
      case 'mail_read_aloud_plan':
        extractMailSourceItems(streamObject).forEach(push);
        break;
      case 'doc_create':
      case 'doc_compose':
        extractCreatedDocSourceItem(streamObject).forEach(push);
        break;
      case 'web_search':
      case 'web_crawl':
        extractWebSourceItems(streamObject).forEach(push);
        break;
      default:
        break;
    }
  }

  return { items };
}

export function reusableTurnSourcesFromManifest(
  manifest: AssistantSourceManifest
): ReusableTurnSource[] {
  return manifest.items.flatMap(item => {
    const snippet = readableText(item.display.preview);
    switch (item.kind) {
      case 'doc-search':
      case 'mail':
      case 'created-doc':
        return [
          {
            key: item.id,
            label: item.label,
            ...(snippet ? { snippet } : {}),
          },
        ];
      case 'web': {
        const url = readableUrl(item.refs.url);
        if (!url) return [];
        return [
          {
            key: item.id,
            label: item.label,
            url,
            ...(snippet ? { snippet } : {}),
          },
        ];
      }
      default:
        return [];
    }
  });
}

function extractDocSearchSourceItems(
  streamObject: StreamObject & { type: 'tool-result' }
): AssistantSourceItem[] {
  return unwrapResultArray(streamObject.result).flatMap((item, index) => {
    const sourceRecord = asRecord(item);
    if (!sourceRecord) return [];

    const docId = readableText(sourceRecord.docId);
    if (!docId) return [];

    const citationId =
      readableCitationId(sourceRecord.citationId) || `${index + 1}`;
    const title =
      readableText(sourceRecord.attachmentName) ||
      titleFromContent(readableText(sourceRecord.content)) ||
      readableText(sourceRecord.title) ||
      readableText(sourceRecord.docTitle) ||
      'Untitled';
    const pageIndex =
      typeof sourceRecord.pageIndex === 'number' &&
      Number.isInteger(sourceRecord.pageIndex)
        ? sourceRecord.pageIndex
        : undefined;
    const page = pageIndex === undefined ? '' : `, Seite ${pageIndex + 1}`;
    const snippet =
      readableText(sourceRecord.snippet) || readableText(sourceRecord.content);
    const blockId = readableText(sourceRecord.blockId);
    const sourceKind = readableText(sourceRecord.sourceKind);
    const sourceType = readableText(sourceRecord.sourceType);
    const elementId = readableText(sourceRecord.elementId);
    const parentBlockId = readableText(sourceRecord.parentBlockId);
    const parentFlavour = readableText(sourceRecord.parentFlavour);
    const noteBlockId = readableText(sourceRecord.noteBlockId);
    const displayMode = readableText(sourceRecord.displayMode);
    const docTitle = readableText(sourceRecord.docTitle);
    const attachmentName = readableText(sourceRecord.attachmentName);
    const databaseId = readableText(sourceRecord.databaseId);
    const databaseRowId = readableText(sourceRecord.databaseRowId);
    const databaseColumnId = readableText(sourceRecord.databaseColumnId);
    const databaseTitle = readableText(sourceRecord.databaseTitle);
    const databaseRowLabel = readableText(sourceRecord.databaseRowLabel);
    const databaseColumnName = readableText(sourceRecord.databaseColumnName);
    const visibility = readableText(sourceRecord.visibility);
    const technicalSourceKind = readableText(sourceRecord.technicalSourceKind);
    const query =
      readableText(streamObject.args?.query) ||
      readableText(sourceRecord.query);

    return [
      {
        id: [
          'doc',
          docId,
          blockId ? `block:${blockId}` : '',
          elementId ? `element:${elementId}` : '',
          databaseId ? `database:${databaseId}` : '',
          databaseRowId ? `row:${databaseRowId}` : '',
          databaseColumnId ? `column:${databaseColumnId}` : '',
          pageIndex !== undefined ? `page:${pageIndex}` : '',
          `citation:${citationId}`,
        ]
          .filter(Boolean)
          .join(':'),
        kind: 'doc-search',
        label: `[${citationId}] ${title}${page}`,
        display: {
          title,
          subtitle: pageIndex === undefined ? null : `Seite ${pageIndex + 1}`,
          icon: sourceKind === 'image' ? 'image' : 'doc',
          preview: snippet || null,
        },
        refs: {
          docId,
          citationId,
          ...(blockId ? { blockId } : {}),
          ...(pageIndex !== undefined ? { pageIndex } : {}),
          ...(sourceKind ? { sourceKind } : {}),
          ...(sourceType ? { sourceType } : {}),
          ...(elementId ? { elementId } : {}),
          ...(parentBlockId ? { parentBlockId } : {}),
          ...(parentFlavour ? { parentFlavour } : {}),
          ...(noteBlockId ? { noteBlockId } : {}),
          ...(displayMode ? { displayMode } : {}),
          ...(docTitle ? { docTitle } : {}),
          ...(attachmentName ? { attachmentName } : {}),
          ...(databaseId ? { databaseId } : {}),
          ...(databaseRowId ? { databaseRowId } : {}),
          ...(databaseColumnId ? { databaseColumnId } : {}),
          ...(databaseTitle ? { databaseTitle } : {}),
          ...(databaseRowLabel ? { databaseRowLabel } : {}),
          ...(databaseColumnName ? { databaseColumnName } : {}),
          ...(visibility ? { visibility } : {}),
          ...(technicalSourceKind ? { technicalSourceKind } : {}),
          ...(query ? { query } : {}),
        },
        payload: jsonRecord({
          ...sourceRecord,
          toolName: streamObject.toolName,
        }),
      },
    ];
  });
}

function extractDocReadSourceItems(
  streamObject: StreamObject & { type: 'tool-result' }
): AssistantSourceItem[] {
  const result = asRecord(streamObject.result);
  if (result?.success === false) return [];

  const data = asRecord(result?.data) ?? result;
  if (!data) return [];

  const docId = readableText(data.docId) || readableText(data.doc_id);
  if (!docId) return [];

  const title = readableText(data.title) || 'Untitled';
  const snippet =
    readableText(data.snippet) ||
    readableText(data.markdown) ||
    readableText(data.content);

  return [
    {
      id: `doc:${docId}:read`,
      kind: 'doc-search',
      label: `[1] ${title}`,
      display: {
        title,
        subtitle: null,
        icon: 'doc',
        preview: snippet || null,
      },
      refs: {
        docId,
        citationId: '1',
        sourceKind: 'doc',
        technicalSourceKind: 'doc_read',
      },
      payload: jsonRecord({
        docId,
        title,
        ...(snippet ? { snippet } : {}),
        toolName: streamObject.toolName,
      }),
    },
  ];
}

function extractDatabaseReadSourceItems(
  streamObject: StreamObject & { type: 'tool-result' }
): AssistantSourceItem[] {
  const result = asRecord(streamObject.result);
  if (result?.success === false) return [];

  const data = asRecord(result?.data) ?? result;
  if (!data) return [];

  const docId = readableText(data.docId);
  const blockId = readableText(data.blockId) || readableText(data.databaseId);
  if (!docId || !blockId) return [];

  const title =
    readableText(data.databaseTitle) || readableText(data.title) || 'Database';
  const offset = readableNumber(data.offset) ?? 0;
  const rows = Array.isArray(data.rows) ? data.rows.length : 0;
  const totalCount = readableNumber(data.totalCount);
  const firstRow = rows > 0 ? offset + 1 : offset;
  const lastRow = rows > 0 ? offset + rows : offset;
  const preview =
    rows > 0
      ? totalCount === undefined
        ? `Zeilen ${firstRow}-${lastRow}`
        : `Zeilen ${firstRow}-${lastRow} von ${totalCount}`
      : totalCount === undefined
        ? 'Keine Zeilen in dieser Ansicht'
        : `Keine Zeilen in dieser Ansicht, ${totalCount} insgesamt`;

  return [
    {
      id: `doc:${docId}:database:${blockId}:rows:${offset}-${rows}`,
      kind: 'doc-search',
      label: `[1] ${title}`,
      display: {
        title,
        subtitle: 'Datenbank',
        icon: 'doc',
        preview,
      },
      refs: {
        docId,
        blockId,
        citationId: '1',
        sourceKind: 'block',
        sourceType: 'database',
        databaseId: blockId,
        databaseTitle: title,
        technicalSourceKind: 'database_read',
      },
      payload: jsonRecord({
        ...data,
        toolName: streamObject.toolName,
      }),
    },
  ];
}

function extractBlobReadSourceItems(
  streamObject: StreamObject & { type: 'tool-result' }
): AssistantSourceItem[] {
  const result = asRecord(streamObject.result);
  if (result?.success === false) return [];

  const data = asRecord(result?.data) ?? result;
  const source = asRecord(data?.source);
  if (!data || !source) return [];

  if (source.technicalSourceKind === 'blob_read_pdf_selection') {
    const selectionRef = readableText(data.selectionRef);
    const docId = readableText(source.docId);
    const blockId = readableText(source.blockId);
    if (!selectionRef || !docId || !blockId) return [];
    const attachmentName = readableText(source.attachmentName) || 'PDF';
    const excerpts = Array.isArray(data.excerpts) ? data.excerpts : [];
    return [
      {
        id: `doc:${docId}:pdf-selection:${selectionRef}:${blockId}`,
        kind: 'doc-search',
        label: `[1] ${attachmentName}`,
        display: {
          title: attachmentName,
          subtitle: 'PDF',
          icon: 'doc',
          preview: excerpts.length ? 'PDF excerpt' : null,
        },
        refs: {
          docId,
          blockId,
          selectionRef,
          citationId: '1',
          sourceKind: 'block',
          sourceType: 'attachment',
          attachmentName,
          technicalSourceKind: 'blob_read_pdf_selection',
        },
        payload: jsonRecord({
          selectionRef,
          docId,
          blockId,
          name: attachmentName,
          status: readableText(data.status),
          excerpts,
          source: {
            docId,
            blockId,
            attachmentName,
            sourceKind: 'block',
            sourceType: 'attachment',
            technicalSourceKind: 'blob_read_pdf_selection',
          },
          toolName: streamObject.toolName,
        }),
      },
    ];
  }

  const docId = readableText(source.docId) || firstReadableText(source.docIds);
  if (!docId) return [];

  const blobKey = readableText(source.blobKey) || readableText(data.key);
  const attachmentName =
    readableText(source.attachmentName) ||
    readableText(source.fileName) ||
    blobKey ||
    'Attachment';
  const mimeType = readableText(data.mimeType);
  const content = readableText(data.content);
  const size = readableNumber(data.size);
  const preview =
    content ||
    [mimeType, size === undefined ? '' : `${size} bytes`]
      .filter(Boolean)
      .join(' · ');
  const blockId = readableText(source.blockId);
  const sourceKind =
    readableDocSourceKind(source.sourceKind) ||
    (mimeType.startsWith('image/') ? 'image' : blockId ? 'block' : 'doc');
  const databaseId = readableText(source.databaseId);
  const databaseTitle = readableText(source.databaseTitle);
  const databaseRowId = readableText(source.databaseRowId);
  const databaseColumnId = readableText(source.databaseColumnId);
  const databaseColumnName = readableText(source.databaseColumnName);

  return [
    {
      id: `doc:${docId}:blob:${blobKey || attachmentName}`,
      kind: 'doc-search',
      label: `[1] ${attachmentName}`,
      display: {
        title: attachmentName,
        subtitle: databaseTitle || mimeType || null,
        icon: sourceKind === 'image' ? 'image' : 'doc',
        preview: preview || null,
      },
      refs: {
        docId,
        citationId: '1',
        sourceKind,
        sourceType: 'attachment',
        attachmentName,
        technicalSourceKind: 'blob_read',
        ...(blobKey ? { blobKey } : {}),
        ...(blockId ? { blockId } : {}),
        ...(databaseId ? { databaseId } : {}),
        ...(databaseTitle ? { databaseTitle } : {}),
        ...(databaseRowId ? { databaseRowId } : {}),
        ...(databaseColumnId ? { databaseColumnId } : {}),
        ...(databaseColumnName ? { databaseColumnName } : {}),
      },
      payload: jsonRecord({
        ...data,
        toolName: streamObject.toolName,
      }),
    },
  ];
}

function extractMailSourceItems(
  streamObject: StreamObject & { type: 'tool-result' }
): AssistantSourceItem[] {
  const rows = unwrapMailSourceRows(streamObject.result);

  return rows.flatMap((row, index) => {
    const messageId = readableText(row.messageId);
    const subject = readableText(row.subject) || 'Mail';
    const from = readableText(row.from);
    const receivedAt = readableText(row.receivedAt);
    const snippet =
      readableText(row.snippet) ||
      readableText(row.bodyExcerpt) ||
      readableText(row.summary);
    const date = receivedAt ? receivedAt.slice(0, 10) : '';
    const meta = [from, date].filter(Boolean).join(', ');
    const id = `mail:${messageId || `${subject}:${from}:${receivedAt}:${index}`}`;

    return [
      {
        id,
        kind: 'mail',
        label: meta ? `Mail: ${subject} (${meta})` : `Mail: ${subject}`,
        display: {
          title: subject,
          subtitle: meta || null,
          icon: 'mail',
          preview: snippet || null,
        },
        refs: {
          ...(messageId ? { messageId } : {}),
          ...(from ? { from } : {}),
          ...(receivedAt ? { receivedAt } : {}),
        },
        payload: jsonRecord({ ...row, toolName: streamObject.toolName }),
      },
    ];
  });
}

function extractCreatedDocSourceItem(
  streamObject: StreamObject & { type: 'tool-result' }
): AssistantSourceItem[] {
  const result = asRecord(streamObject.result);
  if (result?.success === false) return [];

  const data = asRecord(result?.data);
  const docId = readableText(data?.docId);
  if (!docId) return [];

  const title = readableText(data?.title) || 'Untitled';
  return [
    {
      id: `created-doc:${docId}`,
      kind: 'created-doc',
      label: `Erstelltes Dokument: ${title}`,
      display: {
        title,
        subtitle: null,
        icon: 'doc',
        preview: null,
      },
      refs: { docId },
      payload: jsonRecord({ ...(data ?? {}), toolName: streamObject.toolName }),
    },
  ];
}

function extractWebSourceItems(
  streamObject: StreamObject & { type: 'tool-result' }
): AssistantSourceItem[] {
  return unwrapResultArray(streamObject.result).flatMap(item => {
    const recordValue = asRecord(item);
    if (!recordValue) return [];

    const url = readableUrl(recordValue.url);
    if (!url) return [];

    const title = readableText(recordValue.title) || url;
    const snippet =
      readableText(recordValue.snippet) || readableText(recordValue.content);
    return [
      {
        id: `web:${url}`,
        kind: 'web',
        label: title,
        display: {
          title,
          subtitle: url,
          icon: 'web',
          preview: snippet || null,
        },
        refs: { url },
        payload: jsonRecord({
          ...recordValue,
          toolName: streamObject.toolName,
        }),
      },
    ];
  });
}

function unwrapResultArray(result: unknown): unknown[] {
  if (Array.isArray(result)) {
    return result;
  }

  const value = asRecord(result);
  const data = value?.data;
  return Array.isArray(data) ? data : [];
}

function unwrapMailSourceRows(result: unknown): Record<string, unknown>[] {
  const value = asRecord(result);
  const data = asRecord(value?.data) ?? value;
  if (!data) return [];

  const rows: Record<string, unknown>[] = [];
  appendRecord(rows, data.source);
  appendRecords(rows, data.sources);
  appendRecords(rows, data.messages);
  appendRecords(rows, data.drafts);
  return rows;
}

function appendRecords(rows: Record<string, unknown>[], value: unknown): void {
  if (!Array.isArray(value)) return;
  for (const item of value) {
    appendRecord(rows, item);
  }
}

function appendRecord(rows: Record<string, unknown>[], value: unknown): void {
  const valueRecord = asRecord(value);
  if (valueRecord) {
    rows.push(valueRecord);
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function jsonRecord(value: Record<string, unknown>): Record<string, unknown> {
  return value;
}

function readableCitationId(value: unknown): string {
  const text = readableText(value);
  return /^\d+$/.test(text) ? text : '';
}

function readableNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function firstReadableText(value: unknown): string {
  if (!Array.isArray(value)) return '';
  for (const item of value) {
    const text = readableText(item);
    if (text) return text;
  }
  return '';
}

function readableDocSourceKind(
  value: unknown
): 'doc' | 'block' | 'pdf-page' | 'image' | undefined {
  const text = readableText(value);
  return text === 'doc' ||
    text === 'block' ||
    text === 'pdf-page' ||
    text === 'image'
    ? text
    : undefined;
}

function readableText(value: unknown): string {
  return typeof value === 'string'
    ? value
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
    : '';
}

function readableUrl(value: unknown): string {
  const text = readableText(value);
  if (!/^https?:\/\//i.test(text)) return '';
  return text.replace(/[)\s]+$/g, '');
}

function titleFromContent(content: string): string {
  const titleLine = content
    .split('\n')
    .find(line => line.trim().startsWith('Title:'));
  return (
    titleLine
      ?.replace(/^Title:\s*/i, '')
      .replace(/,\s*page\s+\d+\s*$/i, '')
      .trim() ?? ''
  );
}
